import { CompanyFormShape, CompanyModal } from './CompanyModal'
import { toast } from 'react-toastify'
import { IDoorProxy } from '../../../models/contact_companies'
import useApiService from '../../../services/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'

export function EditDoorProxyModal(props: {
  doorProxy: IDoorProxy | null
  show: boolean
  setShow: (show: boolean) => void
}) {
  const api = useApiService()
  const queryClient = useQueryClient()
  const rawProxy = props?.doorProxy?.chain_proxy
  const chainProxyId =
    typeof rawProxy === 'object' && rawProxy !== null
      ? (rawProxy as { id: number }).id
      : rawProxy
  const { refetch } = api.useDoorProxiesOfChainProxy(chainProxyId)

  const apiAction = async (values: CompanyFormShape) => {
    if (!props.doorProxy) {
      return
    }

    const payload = {
      ...values,
      zipcode: values.zipcode || '',
      address: values.address || '',
      country: values.country || 'US',
      state: values.state || '',
      city: values.city || '',
      domain: values.domain?.toLowerCase() || '',
      name: values.name?.trim() || '',
    }
    const response = await api.editDoorProxy(payload, props?.doorProxy?.id)
    await refetch()
    toast.success('Door updated successfully')
    return response
  }

  const { mutateAsync } = useMutation({
    mutationFn: apiAction,
    onMutate: async (values: CompanyFormShape) => {
      if (!props.doorProxy) return

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['door_proxy', props.doorProxy.id],
      })

      // Snapshot the previous value
      const previousDoorProxy = queryClient.getQueryData([
        'door_proxy',
        props.doorProxy.id,
      ])

      // Optimistically update the cache
      queryClient.setQueryData(
        ['door_proxy', props.doorProxy.id],
        (old: IDoorProxy) => {
          if (!old) return old
          return {
            ...old,
            name: values.name?.trim() || old.name,
            domain: values.domain?.toLowerCase() || old.domain,
            country: values.country || old.country,
            state: values.state || old.state,
            city: values.city || old.city,
            address: values.address || old.address,
            zipcode: values.zipcode || old.zipcode,
            phone_number: values.phone_number || old.phone_number,
            extension: values.extension || old.extension,
          }
        }
      )

      // Return context for rollback
      return { previousDoorProxy }
    },
    onError: (err, values, context) => {
      // Rollback on error
      if (context?.previousDoorProxy && props.doorProxy) {
        queryClient.setQueryData(
          ['door_proxy', props.doorProxy.id],
          context.previousDoorProxy
        )
      }
      toast.error('Failed to update door')
    },
    onSettled: () => {
      // Always refetch to ensure consistency
      if (props.doorProxy) {
        queryClient.invalidateQueries({
          queryKey: ['door_proxy', props.doorProxy.id],
        })
      }
    },
  })

  const optimisticApiAction = async (values: CompanyFormShape) => {
    return await mutateAsync(values)
  }

  if (!props.doorProxy) {
    return null
  }

  // Map taglist and distributors to arrays of IDs
  const mappedTaglist = props.doorProxy.taglist?.map((tag) => tag.id)
  const mappedDistributors = props.doorProxy.distributors?.map(
    (dist) => dist.id
  )

  return (
    <CompanyModal
      hideCategoryAndType
      hideKnownDistributorsAndTags
      defaultValues={{
        ...props.doorProxy,
        taglist: mappedTaglist,
        distributors: mappedDistributors,
        category: 0,
        sub_category: 0,
      }}
      apiAction={optimisticApiAction}
      show={props.show}
      setShow={props.setShow}
      type="door"
      title="Edit Door"
      description="Add or modify information about this door."
      acceptButtonText="Apply Changes"
    />
  )
}
